// ════════════════════════════════════════════════════════════════════════════
// SFX — short one-shot sound effects for menu/button interaction and battle
// feedback (assets/sound/sfx/*.wav). Volume is user-controlled via the
// Settings modal's "Game SFX" slider, persisted to localStorage, and
// defaults to 5%.
//
// Playback uses a small fixed pool of pre-created <audio> elements per sound
// (round-robin), instead of cloneNode()-ing a fresh element on every trigger.
// This matters for the autoplay-unlock story below: Chrome's "you may now
// play audio" unlock is document-wide and covers brand-new elements created
// after the gesture, but Firefox's is not reliably so — a freshly
// cloneNode()'d <audio> that was never itself touched during a gesture can
// still get rejected there even after the page is otherwise "unlocked". Using
// a fixed pool means every element that's ever played is one of the same
// elements _unlockSfxOnFirstGesture() primes below, which works on both.
// ════════════════════════════════════════════════════════════════════════════
const SFX_VOLUME_STORAGE_KEY = 'nu-sfx-volume';
const SFX_DEFAULT_VOLUME = 0.05;
const SFX_POOL_SIZE = 4; // concurrent overlapping plays per sound (e.g. fast hover sweeps)

// found_game intentionally omitted — not wired up yet.
const SFX_SOURCES = {
  cancel:      'assets/sound/sfx/cancel.wav',
  card_played: 'assets/sound/sfx/card_played.wav',
  destroyed:   'assets/sound/sfx/destroyed.wav',
  hover:       'assets/sound/sfx/hover.wav',
  select:      'assets/sound/sfx/select.wav',
};

const _sfxPool = {};    // name -> Audio[SFX_POOL_SIZE]
const _sfxPoolIdx = {}; // name -> next round-robin index
Object.keys(SFX_SOURCES).forEach(name => {
  _sfxPool[name] = Array.from({ length: SFX_POOL_SIZE }, () => {
    const a = new Audio(SFX_SOURCES[name]);
    a.preload = 'auto';
    return a;
  });
  _sfxPoolIdx[name] = 0;
});

function getSfxVolume() {
  try {
    const raw = localStorage.getItem(SFX_VOLUME_STORAGE_KEY);
    if (raw === null) return SFX_DEFAULT_VOLUME;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : SFX_DEFAULT_VOLUME;
  } catch {
    return SFX_DEFAULT_VOLUME;
  }
}

function setSfxVolume(v) {
  const clamped = Math.min(1, Math.max(0, v));
  try { localStorage.setItem(SFX_VOLUME_STORAGE_KEY, String(clamped)); } catch {}
}

function playSfx(name) {
  const pool = _sfxPool[name];
  if (!pool) return;
  const volume = getSfxVolume();
  if (volume <= 0) return;
  // Round-robin through the pool so an overlapping trigger (e.g. rapid-fire
  // hover) gets its own free playhead instead of cutting off one still
  // finishing, without creating a new element per play (see header comment).
  const node = pool[_sfxPoolIdx[name]];
  _sfxPoolIdx[name] = (_sfxPoolIdx[name] + 1) % pool.length;
  node.currentTime = 0;
  node.volume = volume;
  node.play().catch(() => {}); // ignore autoplay-policy rejections (e.g. no gesture yet — see _unlockSfxOnFirstGesture)
}

// Browsers block audio.play() unless it's triggered by, or happens after, a
// genuine user gesture (mousedown/keydown/touchstart — NOT mouseover/
// mousemove). Before that first gesture, every hover-triggered play() above
// is silently rejected (NotAllowedError), which reads as "hover sound doesn't
// work" until the player happens to click something.
//
// Prime every element in every pool here — not just one representative per
// sound — from directly within the gesture handler (capture phase, as early
// as pointerdown/keydown fire) so whichever pool element playSfx() picks
// later via round-robin has already been played-and-paused under a real
// gesture, on every browser's autoplay model, not just Chrome's page-wide one.
function _unlockSfxOnFirstGesture() {
  const unlock = () => {
    Object.values(_sfxPool).forEach(pool => pool.forEach(a => {
      const wasMuted = a.muted;
      a.muted = true;
      a.play().then(() => { a.pause(); a.currentTime = 0; a.muted = wasMuted; }).catch(() => { a.muted = wasMuted; });
    }));
    document.removeEventListener('pointerdown', unlock, true);
    document.removeEventListener('keydown', unlock, true);
  };
  document.addEventListener('pointerdown', unlock, true);
  document.addEventListener('keydown', unlock, true);
}

// ── Global delegation ───────────────────────────────────────────────────────
// Every clickable control in this app is a real <button> element (verified
// across main menu, deck builder, settings, and in-game modals), so a single
// document-level listener pair covers hover/select/cancel everywhere without
// having to wire each button individually.
function _isCancelOrBackButton(el) {
  const idClass = `${el.id || ''} ${el.className || ''}`;
  if (/cancel|back|close|skip/i.test(idClass)) return true;
  const text = (el.textContent || '').trim().toLowerCase();
  return text === 'cancel' || text === 'back' || text === 'close' || text === '← back';
}

function initSfxDelegation() {
  // 'mouseover' bubbles (unlike 'mouseenter'), so it works with delegation.
  // The relatedTarget check stops it from re-firing while the cursor moves
  // between child elements inside the same button (e.g. icon + label spans).
  document.addEventListener('mouseover', (e) => {
    const btn = e.target.closest?.('button');
    if (!btn || btn.disabled) return;
    if (btn.contains(e.relatedTarget)) return;
    playSfx('hover');
  });

  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('button');
    if (!btn || btn.disabled) return;
    playSfx(_isCancelOrBackButton(btn) ? 'cancel' : 'select');
  });
}

// ── Settings modal widget ───────────────────────────────────────────────────
function renderSfxVolumeControlHTML() {
  const pct = Math.round(getSfxVolume() * 100);
  return `
    <div class="sfx-volume-control">
      <input type="range" class="sfx-volume-slider" min="0" max="100" step="1" value="${pct}" />
      <span class="sfx-volume-value">${pct}%</span>
    </div>
  `;
}

function wireSfxVolumeControl(containerEl) {
  const wrap = containerEl.classList?.contains('sfx-volume-control')
    ? containerEl
    : containerEl.querySelector('.sfx-volume-control');
  if (!wrap) return;
  const slider = wrap.querySelector('.sfx-volume-slider');
  const valueEl = wrap.querySelector('.sfx-volume-value');
  slider.addEventListener('input', () => {
    const pct = parseInt(slider.value, 10) || 0;
    valueEl.textContent = `${pct}%`;
    setSfxVolume(pct / 100);
  });
  // Preview on release rather than every tick, so dragging doesn't spam plays.
  slider.addEventListener('change', () => playSfx('select'));
}

// Delegated listeners work as soon as they're attached — no need to wait for
// DOMContentLoaded, since document itself already exists at script-eval time.
initSfxDelegation();
_unlockSfxOnFirstGesture();
